import type { CSSProperties } from "react";
import type { Theme } from "../../../providers/ThemeProvider";
import type { MetricCardPayload } from "../types";
import type {
  MealItemScoreTone,
  MealItemStatusTag,
} from "../custom-cards/mealitems/types";
import type { WorkoutStateTag } from "../custom-cards/workoutdetails/types";

type SemanticAppearance = Pick<
  CSSProperties,
  "backgroundColor" | "borderColor" | "boxShadow" | "color"
>;

export const getMetricStatusAppearance = (
  theme: Theme,
  status: MetricCardPayload["status"]
): SemanticAppearance => {
  switch (status) {
    case "good":
      return {
        backgroundColor: theme.colorTokens.S[10],
        color: theme.colorTokens.S[80],
      };
    case "watch":
      return {
        backgroundColor: theme.colorTokens.W[10],
        color: theme.colorTokens.W[90],
      };
    case "risk":
      return {
        backgroundColor: theme.colorTokens.D[10],
        color: theme.colorTokens.D[80],
      };
  }
};

export const getMetricTrendColor = (
  theme: Theme,
  trend: MetricCardPayload["trend"]
) => {
  if (trend === "up") {
    return theme.colorTokens.S[80];
  }

  if (trend === "down") {
    return theme.colorTokens.D[70];
  }

  return theme.colors_ds.NEUTRAL_TEXT_WEAK;
};

export const getBiomarkerValueColor = (
  theme: Theme,
  scoreCategory: number
) => {
  switch (scoreCategory) {
    case 1:
      return theme.colorTokens.W[80];
    case 2:
      return theme.colorTokens.P[80];
    case 3:
      return theme.colorTokens.S[80];
    case 4:
      return theme.colorTokens.D[80];
    default:
      return theme.colors_ds.NEUTRAL_TEXT_STRONG;
  }
};

export const getMealStatusAppearance = (
  theme: Theme,
  status: MealItemStatusTag
): SemanticAppearance => {
  switch (status) {
    case "logged":
    case "nutritionist_approved":
      return {
        backgroundColor: theme.colorTokens.S[10],
        color: theme.colorTokens.S[80],
      };
    case "skipped":
      return {
        backgroundColor: theme.colorTokens.D[10],
        color: theme.colorTokens.D[80],
      };
    case "swapped":
      return {
        backgroundColor: theme.colorTokens.P[10],
        color: theme.colorTokens.P[80],
      };
    case "extra":
      return {
        backgroundColor: theme.colorTokens.W[10],
        color: theme.colorTokens.W[90],
      };
    case "personalized":
      return {
        backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
        color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
  }
};

export const getMealScoreAppearance = (
  theme: Theme,
  tone: MealItemScoreTone
): SemanticAppearance => {
  switch (tone) {
    case "good":
      return {
        backgroundColor: theme.colorTokens.S[10],
        color: theme.colorTokens.S[80],
        borderColor: theme.colorTokens.S[20],
      };
    case "watch":
      return {
        backgroundColor: theme.colorTokens.W[10],
        color: theme.colorTokens.W[90],
        borderColor: theme.colorTokens.W[20],
      };
    case "risk":
      return {
        backgroundColor: theme.colorTokens.D[10],
        color: theme.colorTokens.D[80],
        borderColor: theme.colorTokens.D[20],
      };
  }
};

export const getWorkoutStateAppearance = (
  theme: Theme,
  state: WorkoutStateTag
): SemanticAppearance => {
  switch (state) {
    case "completed":
      return {
        backgroundColor: theme.colorTokens.S[10],
        color: theme.colorTokens.S[80],
      };
    case "in_progress":
      return {
        backgroundColor: theme.colorTokens.W[10],
        color: theme.colorTokens.W[90],
      };
    case "scheduled":
      return {
        backgroundColor: theme.colorTokens.P[10],
        color: theme.colorTokens.P[80],
      };
    case "automatically_logged":
      return {
        backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
        color: theme.colors_ds.PRIMARY_TEXT_MAIN,
      };
    case "manually_logged":
      return {
        backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
        color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      };
  }
};
